import { Encounter, Medicine, Patient, StockMovement } from '../types'
import { CatalogueItem, ChargeClaim, PendingCharge } from '../types/billing'

/**
 * Builds the list of hospital charges that have not yet been billed.
 *
 * Every charge has a deterministic source reference, and a reference that has
 * been claimed by an invoice is excluded — so the same consultation, test, scan
 * or dispensed medicine can never be billed twice, whatever is retried.
 * Pharmacy charges come from stock movements already recorded at dispensing;
 * billing never touches stock.
 */

const norm = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ')
const slug = (value: string) => norm(value).replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

export function matchItem(items: CatalogueItem[], sourceType: CatalogueItem['sourceType'], names: string[]) {
  const wanted = names.map(norm)
  const active = items.filter((i) => i.status === 'Active')
  return (
    active.find((i) => i.sourceType === sourceType && (wanted.includes(norm(i.name)) || i.matchNames.some((m) => wanted.includes(norm(m))))) ??
    active.find((i) => wanted.includes(norm(i.name)) || i.matchNames.some((m) => wanted.includes(norm(m))))
  )
}

export function collectHospitalCharges(input: {
  encounters: Encounter[]
  patients: Patient[]
  medicines: Medicine[]
  stockMovements: StockMovement[]
  items: CatalogueItem[]
  claims: ChargeClaim[]
}): PendingCharge[] {
  const claimed = new Set(input.claims.map((c) => c.id))
  const patients = new Map(input.patients.map((p) => [p.id, p]))
  const charges: PendingCharge[] = []

  const push = (charge: Omit<PendingCharge, 'patientName' | 'bmcFileNumber' | 'unitPrice'> & { unitPrice?: number | null }) => {
    if (claimed.has(charge.sourceRef.replace(/\//g, '_'))) return
    const patient = patients.get(charge.patientId)
    if (!patient) return
    charges.push({
      ...charge,
      patientName: `${patient.firstName} ${patient.lastName}`.trim(),
      bmcFileNumber: patient.bmcFileNumber,
      unitPrice: charge.unitPrice ?? (charge.item ? charge.item.unitPrice : null),
    })
  }

  for (const enc of input.encounters) {
    const label = `Visit ${enc.startedAt.slice(0, 10)}`
    const date = enc.startedAt.slice(0, 10)

    if (enc.consultationType || enc.clinicalNote) {
      const type = enc.consultationType === 'Gyne' ? 'Gyne Consultation' : enc.consultationType === 'General' ? 'General Consultation' : 'Consultation'
      push({
        sourceRef: `enc:${enc.id}:consult`, kind: 'Consultation', patientId: enc.patientId, encounterId: enc.id, encounterLabel: label, date,
        description: type, quantity: 1, item: matchItem(input.items, 'consultation', [type, 'Consultation', 'Consultation Fee']),
      })
    }

    for (const req of enc.labRequests ?? []) {
      if (req.status === 'Cancelled') continue
      const removed = new Set((req.removedTestNames ?? []).map(norm))
      const tests = [...(req.tests ?? []), ...(req.otherTests ? req.otherTests.split(/[,;\n]/) : [])]
        .map((t) => t.trim())
        .filter((t) => t && !removed.has(norm(t)))
      for (const test of tests) {
        push({
          sourceRef: `enc:${enc.id}:lab:${req.id}:${slug(test)}`, kind: 'Laboratory', patientId: enc.patientId, encounterId: enc.id, encounterLabel: label,
          date: req.requestedAt.slice(0, 10), description: test, quantity: 1, item: matchItem(input.items, 'lab', [test]),
        })
      }
    }

    for (const req of enc.radiologyRequests ?? []) {
      push({
        sourceRef: `enc:${enc.id}:rad:${req.id}`, kind: 'Radiology', patientId: enc.patientId, encounterId: enc.id, encounterLabel: label,
        date: req.requestedAt.slice(0, 10), description: req.scanType, quantity: 1, item: matchItem(input.items, 'radiology', [req.scanType]),
      })
    }
  }

  const reversed = new Set(input.stockMovements.filter((m) => m.reversalOfMovementId).map((m) => m.reversalOfMovementId))
  const medicines = new Map(input.medicines.map((m) => [m.id, m]))
  const encounters = new Map(input.encounters.map((e) => [e.id, e]))
  for (const mv of input.stockMovements) {
    // Direct-sale movements are already billed on their receipt.
    if (mv.type !== 'Dispense' || mv.referenceType === 'Direct Sale' || reversed.has(mv.id) || !mv.patientId) continue
    const medicine = medicines.get(mv.medicineId)
    const enc = mv.encounterId ? encounters.get(mv.encounterId) : undefined
    push({
      sourceRef: `disp:${mv.id}`, kind: 'Pharmacy', patientId: mv.patientId, encounterId: mv.encounterId ?? '',
      encounterLabel: enc ? `Visit ${enc.startedAt.slice(0, 10)}` : 'Pharmacy', date: mv.createdAt.slice(0, 10),
      description: medicine ? `${medicine.genericName || medicine.name}${medicine.strength ? ` ${medicine.strength}` : ''}` : mv.medicineName,
      quantity: Math.abs(mv.quantityDelta) || 1,
      unitPrice: medicine && medicine.sellingPrice > 0 ? medicine.sellingPrice : null,
    })
  }

  return charges.sort((a, b) => b.date.localeCompare(a.date))
}
